import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import { css, keyframes } from '@emotion/css';
import meridianOrangePalette from '../theme/meridian-orange';
import { createTransition, motionDurations, motionEasing, reducedMotionQuery } from '../theme/motion';

const indeterminateSlideKeyframes = keyframes({
  from: { transform: 'translateX(-100%)' },
  to: { transform: 'translateX(250%)' },
});

const shineKeyframes = keyframes({
  from: { backgroundPosition: '200% 0' },
  to: { backgroundPosition: '-200% 0' },
});

interface InlineProgressProps {
  label: string;
  active: boolean;
  completed: number;
  total: number;
}

// Always rendered at a fixed height and faded with opacity, so starting or finishing a load never
// shifts the content below it.
export default function InlineProgress({ label, active, completed, total }: InlineProgressProps) {
  const meridianTheme = useMeridianTheme();
  const isDeterminate = total > 0;
  const percentage = isDeterminate ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  const fillGradient = `linear-gradient(90deg, ${meridianOrangePalette.palette300} 0%, ${meridianOrangePalette.palette400} 100%)`;

  const trackClass = css({
    position: 'relative',
    height: 3,
    flex: '0 0 auto',
    overflow: 'hidden',
    borderRadius: 999,
    backgroundColor: meridianTheme('themeBorderSubdued'),
    opacity: active ? 1 : 0,
    transition: createTransition(['opacity'], motionDurations.moderate),
    [reducedMotionQuery]: { transition: 'none' },
  });
  const determinateFillClass = css({
    height: '100%',
    borderRadius: 999,
    backgroundImage: `linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.45) 50%, transparent 100%), ${fillGradient}`,
    backgroundSize: '200% 100%, 100% 100%',
    animation: `${shineKeyframes} 1.6s linear infinite`,
    transition: `width ${motionDurations.moderate} ${motionEasing.standard}`,
    [reducedMotionQuery]: { animation: 'none', transition: 'none' },
  });
  const indeterminateFillClass = css({
    width: '40%',
    height: '100%',
    borderRadius: 999,
    backgroundImage: fillGradient,
    animation: `${indeterminateSlideKeyframes} 1.2s ${motionEasing.standard} infinite`,
    [reducedMotionQuery]: { animation: 'none', width: '100%', opacity: 0.6 },
  });

  return (
    <div
      className={trackClass}
      role={active ? 'progressbar' : undefined}
      aria-hidden={active ? undefined : true}
      aria-label={active ? label : undefined}
      aria-valuemin={active ? 0 : undefined}
      aria-valuemax={active && isDeterminate ? total : undefined}
      aria-valuenow={active && isDeterminate ? completed : undefined}
    >
      {isDeterminate ? (
        <div className={determinateFillClass} style={{ width: `${percentage}%` }} />
      ) : (
        <div className={indeterminateFillClass} />
      )}
    </div>
  );
}
